import React from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { CollaborativeGroupWorkCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const TeacherRoleStep: React.FC<
  StepComponentProps<CollaborativeGroupWorkCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();

  const teacherRoleOptions = [
    {
      id: "facilitator",
      emoji: "🎯",
      label: t("collaborative.role.facilitator", "Facilitator"),
      desc: t(
        "collaborative.role.facilitatorDesc",
        "Guide students through the process without direct instruction",
      ),
    },
    {
      id: "observer",
      emoji: "👁️",
      label: t("collaborative.role.observer", "Observer"),
      desc: t(
        "collaborative.role.observerDesc",
        "Monitor group dynamics and take notes on progress",
      ),
    },
    {
      id: "active_guide",
      emoji: "🚀",
      label: t("collaborative.role.activeGuide", "Active Guide"),
      desc: t(
        "collaborative.role.activeGuideDesc",
        "Actively participate and provide direction when needed",
      ),
    },
    {
      id: "coach",
      emoji: "🏆",
      label: t("collaborative.role.coach", "Coach"),
      desc: t(
        "collaborative.role.coachDesc",
        "Support and mentor students through challenges",
      ),
    },
  ];

  const classroomEnvironmentOptions = [
    {
      id: "face_to_face",
      emoji: "🏫",
      label: t("collaborative.environment.faceToFace", "Face to Face"),
      desc: t(
        "collaborative.environment.faceToFaceDesc",
        "Traditional in-person classroom setting",
      ),
    },
    {
      id: "hybrid",
      emoji: "🔄",
      label: t("collaborative.environment.hybrid", "Hybrid"),
      desc: t(
        "collaborative.environment.hybridDesc",
        "Combination of in-person and online elements",
      ),
    },
    {
      id: "online",
      emoji: "💻",
      label: t("collaborative.environment.online", "Online"),
      desc: t(
        "collaborative.environment.onlineDesc",
        "Fully virtual classroom environment",
      ),
    },
  ];

  const physicalLayoutOptions = [
    {
      id: "flexible_seating",
      emoji: "🪑",
      label: t("collaborative.layout.flexibleSeating", "Flexible Seating"),
      desc: t(
        "collaborative.layout.flexibleSeatingDesc",
        "Movable chairs and adaptable arrangements",
      ),
    },
    {
      id: "movable_desks",
      emoji: "📋",
      label: t("collaborative.layout.movableDesks", "Movable Desks"),
      desc: t(
        "collaborative.layout.movableDesksDesc",
        "Desks that can be rearranged for group work",
      ),
    },
    {
      id: "fixed_rows",
      emoji: "📏",
      label: t("collaborative.layout.fixedRows", "Fixed Rows"),
      desc: t(
        "collaborative.layout.fixedRowsDesc",
        "Traditional classroom with fixed desk arrangements",
      ),
    },
    {
      id: "round_tables",
      emoji: "⭕",
      label: t("collaborative.layout.roundTables", "Round Tables"),
      desc: t(
        "collaborative.layout.roundTablesDesc",
        "Circular tables encouraging face-to-face interaction",
      ),
    },
  ];

  const handleTeacherRoleSelect = (role: string) => {
    onDataChange({ teacherRole: role });
  };

  const handleEnvironmentSelect = (environment: string) => {
    onDataChange({ classroomEnvironment: environment });
  };

  const handleLayoutSelect = (layout: string) => {
    onDataChange({ physicalLayout: layout });
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t(
            "collaborative.steps.teacherRole.title",
            "Teacher Role & Environment",
          )}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t(
            "collaborative.steps.teacherRole.description",
            "Define your role and the classroom environment",
          )}
        </p>
      </div>

      {/* Teacher Role Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("collaborative.form.teacherRole", "Teacher Role")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {teacherRoleOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.teacherRole === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleTeacherRoleSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.teacherRole === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.teacherRole === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Classroom Environment Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t(
              "collaborative.form.classroomEnvironment",
              "Classroom Environment",
            )}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {classroomEnvironmentOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.classroomEnvironment === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleEnvironmentSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.classroomEnvironment === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.classroomEnvironment === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Physical Layout Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("collaborative.form.physicalLayout", "Physical Layout")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {physicalLayoutOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.physicalLayout === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleLayoutSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.physicalLayout === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.physicalLayout === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
